<template>
  <TruncatedTextReveal class="position-relative">
    <DataTable
      ref="statementsTable"
      v-model:filters="filters"
      :value="rows"
      dataKey="id"
      filterDisplay="row"
      :globalFilterFields="[
        'beneficiary_name',
        'registry_code',
        'registry_country_region',
        'mwh_cancelled',
      ]"
      class="cancellation-statements-datatable border rounded"
      :sortField="'created_at'"
      :sortOrder="-1"
      v-bind="virtualScrollProps"
      @filter="resetScroll"
      @sort="resetScroll"
      @value-change="(visibleRows) => $emit('visible-change', visibleRows)"
    >
      <template #empty>
        <div class="text-center py-4 text-muted">
          {{ $t("cancellation.no_cancellations") }}
        </div>
      </template>

      <!-- ID Column (admin only) -->
      <Column
        v-if="isAdmin"
        field="id"
        :header="t('cancellation.table.id')"
        sortable
        :headerStyle="{ width: '4rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <font-awesome-icon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span class="alert alert-subtle-warning p-1 mb-0">
            #{{ data.id }}
          </span>
        </template>
      </Column>

      <!-- Created (a bulk upload's statements share one day, so this is how a batch is isolated) -->
      <Column
        field="created_at"
        :header="t('cancellation.table.created')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '11rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <font-awesome-icon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          {{ formatDateTime(data.created_at) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <DatePicker
            v-model="filterModel.value"
            selectionMode="range"
            :manualInput="false"
            dateFormat="dd/mm/yy"
            :placeholder="t('common.filter')"
            showButtonBar
            appendTo="body"
            class="p-column-filter w-100"
            @date-select="filterCallback()"
            @clear-click="filterCallback()"
          />
        </template>
      </Column>

      <!-- Company Column (multi-company view) -->
      <Column
        v-if="showCompanyColumn"
        field="company_id"
        :header="t('common.company')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #sorticon="{ sortOrder }">
          <font-awesome-icon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="uniqueCompanies"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            :focusOnHover="false"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
        <template #body="{ data }">
          <div class="text-truncate">
            {{ data.company_label }}
          </div>
        </template>
      </Column>

      <!-- Beneficiary Name -->
      <Column
        field="beneficiary_name"
        :header="t('cancellation.table.beneficiary_name')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #sorticon="{ sortOrder }">
          <font-awesome-icon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            :placeholder="t('common.search')"
          />
        </template>
        <template #body="{ data }">
          <div class="text-truncate">
            {{ data.beneficiary_name }}
          </div>
        </template>
      </Column>

      <!-- Registry Code -->
      <Column
        field="registry_code"
        :header="t('cancellation.table.registry_code')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #sorticon="{ sortOrder }">
          <font-awesome-icon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            :placeholder="t('common.search')"
          />
        </template>
        <template #body="{ data }">
          <div class="text-truncate">
            {{ data.registry_code ?? "-" }}
          </div>
        </template>
      </Column>

      <!-- Period -->
      <Column
        field="period_key"
        sortField="period_start"
        :header="t('cancellation.table.period')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #sorticon="{ sortOrder }">
          <font-awesome-icon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="uniquePeriods"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            :focusOnHover="false"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
        <template #body="{ data }">
          <div class="text-truncate">
            {{ data.period_label }}
          </div>
        </template>
      </Column>

      <!-- Registry -->
      <Column
        field="registry_country_region"
        :header="t('cancellation.table.registry')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #sorticon="{ sortOrder }">
          <font-awesome-icon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="uniqueRegistries"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            :focusOnHover="false"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
        <template #body="{ data }">
          <div class="text-truncate">
            {{ data.registry_label }}
          </div>
        </template>
      </Column>

      <!-- MWh Cancelled -->
      <Column
        field="mwh_cancelled"
        :header="t('cancellation.table.mwh_cancelled')"
        sortable
        dataType="numeric"
      >
        <template #sorticon="{ sortOrder }">
          <font-awesome-icon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span class="fw-medium">{{ data.mwh_cancelled }} MWh</span>
        </template>
      </Column>

      <!-- Actions -->
      <Column
        :header="t('common.actions.title')"
        :headerStyle="{ width: '6rem' }"
      >
        <template #body="{ data }">
          <div class="d-flex gap-1">
            <button
              class="btn btn-sm btn-icon btn-outline-primary"
              @click="$emit('view-details', data)"
              :title="t('cancellation.view_details')"
            >
              <font-awesome-icon :icon="['fas', 'eye']" />
            </button>
            <button
              v-if="data.uploaded_document_count > 0"
              class="btn btn-sm btn-icon btn-outline-secondary position-relative"
              @click="$emit('download-documents', data)"
              :title="t('cancellation.download_documents')"
            >
              <font-awesome-icon :icon="['fas', 'download']" />
              <span
                class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-success badge-count"
              >
                {{ data.uploaded_document_count }}
              </span>
            </button>
          </div>
        </template>
      </Column>
    </DataTable>
    <ScrollMoreIndicator
      :scrollTarget="scrollElement"
      :itemHeight="ROW_HEIGHT_PX"
    >
      <template #default="{ count }">
        ~{{ t("cancellation.table.more_statements", { count }) }}
      </template>
    </ScrollMoreIndicator>
  </TruncatedTextReveal>
</template>

<script setup lang="ts">
import ScrollMoreIndicator from "@/components/common/ScrollMoreIndicator.vue";
import TruncatedTextReveal from "@/components/common/TruncatedTextReveal.vue";
import { formatDateTime, isFullYearRange } from "@/composables/formatUtils";
import {
  useVirtualScrolledTable,
  type VirtualScrolledTable,
} from "@/composables/useVirtualScrolledTable";
import type { Company } from "@/types";
import type { Cancellation } from "@/types/cancellation";
import { DATE_RANGE_MATCH_MODE } from "@/utils/primevueFilters";
import { getRegistryLabel } from "@/utils/registries";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import DatePicker from "primevue/datepicker";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { getSortIcon } from "@/utils/sortIcons";

// Presentational half of the cancellation statements list: the DataTable, its filters and cell
// formatting. Data loading, drag-drop upload, bulk download and the modals stay in
// CancellationStatementsComponent, so this table can also render rows that never came from the API
// (the Compliance Groups demo feeds it fixture rows).
//
// A bulk cancellation creates hundreds of statements at once, so long lists are virtualised
// (useVirtualScrolledTable), which requires every row to be exactly ROW_HEIGHT_PX tall. DataTables
// lay columns out at fixed widths (primevue-unstyled.css), so text that can outgrow its column
// truncates on one line rather than wrapping, and TruncatedTextReveal shows it in full on hover.
const props = defineProps<{
  cancellations: Cancellation[];
  companies: Pick<Company, "id" | "name">[];
  isAdmin: boolean;
  showCompanyColumn: boolean;
}>();

/**
 * @event visible-change - Every row passing the current filters (not only those in the viewport),
 *   re-emitted whenever the filters or the rows change. Not emitted for an empty row list.
 */
defineEmits<{
  "view-details": [cancellation: Cancellation];
  "download-documents": [cancellation: Cancellation];
  "visible-change": [cancellations: Cancellation[]];
}>();

// PERF: must match the rendered row height or the scroller's offsets drift. Measured in Chrome:
// 0.5rem cell padding top and bottom + the 2rem icon buttons + the 1px row border = 49px.
const ROW_HEIGHT_PX = 49;

const { t } = useI18n();

// The period column filters on a composite key and the cells show formatted labels; derive both
// here, once per row, so callers can pass plain Cancellation rows (the API list and the demo fixture
// alike).
const rows = computed(() =>
  props.cancellations.map((c) => ({
    ...c,
    period_key: `${c.period_start}|${c.period_end}`,
    company_label: getCompanyName(c.company_id) || "-",
    period_label: formatPeriod(c.period_start, c.period_end),
    registry_label: formatRegistry(c.registry_country_region),
  }))
);

const statementsTable = useTemplateRef<VirtualScrolledTable>("statementsTable");
const {
  tableProps: virtualScrollProps,
  resetScroll,
  scrollElement,
} = useVirtualScrolledTable(
  statementsTable,
  () => rows.value.length,
  ROW_HEIGHT_PX
);

// Filters
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  created_at: {
    value: null as Date[] | null,
    matchMode: DATE_RANGE_MATCH_MODE,
  },
  company_id: { value: [], matchMode: FilterMatchMode.IN },
  registry_code: { value: null, matchMode: FilterMatchMode.CONTAINS },
  beneficiary_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  period_key: { value: [] as string[], matchMode: FilterMatchMode.IN },
  registry_country_region: { value: [], matchMode: FilterMatchMode.IN },
});

// Filter options
const uniqueCompanies = computed(() => {
  const companies = new Set(props.cancellations.map((c) => c.company_id));
  return [...companies].map((companyId) => ({
    label: getCompanyName(companyId) || String(companyId),
    value: companyId,
  }));
});

const uniqueRegistries = computed(() => {
  const registries = new Set(
    props.cancellations
      .map((c) => c.registry_country_region)
      .filter((r): r is string => !!r)
  );
  return [...registries].sort().map((registry) => ({
    label: formatRegistry(registry),
    value: registry,
  }));
});

const uniquePeriods = computed(() => {
  const periods = new Map<string, { label: string; start: string }>();
  for (const row of rows.value) {
    if (row.period_start && !periods.has(row.period_key)) {
      periods.set(row.period_key, {
        label: row.period_label,
        start: row.period_start,
      });
    }
  }
  return [...periods.entries()]
    .sort((a, b) => b[1].start.localeCompare(a[1].start))
    .map(([value, { label }]) => ({ label, value }));
});

// Methods
const getCompanyName = (companyId: number) => {
  const company = props.companies.find((company) => company.id === companyId);
  return company ? company.name : "";
};

const formatPeriod = (periodStart: string, periodEnd: string): string => {
  if (!periodStart || !periodEnd) return "-";

  const startDate = new Date(periodStart);
  const endDate = new Date(periodEnd);

  if (isFullYearRange(startDate, endDate)) {
    return String(startDate.getUTCFullYear());
  }

  // Format as date range using UTC values to avoid timezone shifts
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(date);
  };

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

const formatRegistry = (registryCode: string | null | undefined): string => {
  return getRegistryLabel(registryCode, t);
};
</script>

<style scoped lang="scss">
.cancellation-statements-datatable {
  :deep(.pv-datatable-container) {
    overflow-x: auto;
  }

  :deep(.pv-datatable-table) {
    min-width: 64rem;
  }

  :deep(.pv-datatable-body-row) {
    height: v-bind("`${ROW_HEIGHT_PX}px`");
  }
}

.btn-icon {
  width: 2rem;
  height: 2rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.badge-count {
  font-size: 0.6rem;
  padding: 0.2em 0.4em;
  min-width: 1rem;
}

:deep(.pv-datatable-header-cell) {
  padding: 0.5rem 0.75rem;
}

:deep(.pv-datatable-body-cell) {
  padding: 0.5rem 0.75rem;
}
</style>
